import { useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Plus, X, Loader2, GitCompare } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { property_type: "apartament", city: "bucuresti", neighborhood: "", surface: "", rooms: "", year_built: "", condition: "bun", features: [] };

export default function Compare() {
  const [props, setProps] = useState([{ ...EMPTY }, { ...EMPTY }]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const addProp = () => { if (props.length < 4) setProps([...props, { ...EMPTY }]); };
  const removeProp = (i) => { if (props.length > 2) setProps(props.filter((_, idx) => idx !== i)); };
  const setField = (i, k, v) => setProps(props.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const run = async () => {
    setLoading(true);
    try {
      const payloads = props.map(p => ({
        ...p,
        surface: Number(p.surface),
        rooms: p.rooms ? Number(p.rooms) : null,
        year_built: p.year_built ? Number(p.year_built) : null,
      }));
      // Validate
      for (const p of payloads) if (!p.surface || !p.city) throw new Error("Completează oraș și suprafață pentru toate proprietățile");
      const results = await Promise.all(payloads.map(p => api.post("/evaluate", p).then(r => r.data)));
      setResults(results);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Eroare"); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full border-b border-[#0D1F2D] bg-transparent py-2 text-sm font-display focus:outline-none focus:border-[#0F6E5B]";

  return (
    <main data-testid="compare-page" className="bg-[#F6F4EE] min-h-screen text-[#0D1F2D]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Comparație</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight mt-3">Compară până la 4 proprietăți.</h1>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {props.map((p, i) => (
            <div key={i} className="bg-white border border-[#DEDCCF] p-5 relative">
              <div className="flex items-center justify-between">
                <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Proprietate {String(i+1).padStart(2,"0")}</div>
                {props.length > 2 && (
                  <button onClick={() => removeProp(i)} data-testid={`remove-${i}`} className="text-[#52616B] hover:text-[#0F6E5B]"><X size={16} /></button>
                )}
              </div>
              <div className="mt-4 space-y-4">
                <select data-testid={`cmp-type-${i}`} className={inputCls} value={p.property_type} onChange={e => setField(i, "property_type", e.target.value)}>
                  <option value="apartament">Apartament</option><option value="casa">Casă</option>
                  <option value="teren">Teren</option><option value="comercial">Comercial</option>
                </select>
                <select data-testid={`cmp-city-${i}`} className={inputCls} value={p.city} onChange={e => setField(i, "city", e.target.value)}>
                  <option value="bucuresti">București</option><option value="cluj-napoca">Cluj-Napoca</option>
                  <option value="timisoara">Timișoara</option><option value="iasi">Iași</option>
                  <option value="brasov">Brașov</option><option value="constanta">Constanța</option>
                  <option value="sibiu">Sibiu</option><option value="oradea">Oradea</option>
                </select>
                <input data-testid={`cmp-surface-${i}`} type="number" placeholder="Suprafață (m²)" className={inputCls} value={p.surface} onChange={e => setField(i, "surface", e.target.value)} />
                <input data-testid={`cmp-rooms-${i}`} type="number" placeholder="Camere" className={inputCls} value={p.rooms} onChange={e => setField(i, "rooms", e.target.value)} />
                <input data-testid={`cmp-year-${i}`} type="number" placeholder="An construcție" className={inputCls} value={p.year_built} onChange={e => setField(i, "year_built", e.target.value)} />
                <select data-testid={`cmp-cond-${i}`} className={inputCls} value={p.condition} onChange={e => setField(i, "condition", e.target.value)}>
                  <option value="lux">Lux</option><option value="renovat">Renovat</option>
                  <option value="bun">Bun</option><option value="necesita_renovare">Necesită renovare</option>
                </select>
              </div>
            </div>
          ))}
          {props.length < 4 && (
            <button onClick={addProp} data-testid="add-prop" className="border border-dashed border-[#0D1F2D] p-5 flex flex-col items-center justify-center text-[#52616B] hover:text-[#0F6E5B] hover:border-[#0F6E5B] transition-colors min-h-[200px]">
              <Plus size={28} /><span className="mt-2 text-sm">Adaugă proprietate</span>
            </button>
          )}
        </div>

        <button data-testid="compare-run" onClick={run} disabled={loading} className="mt-8 bg-[#0F6E5B] hover:bg-[#0A5447] text-white px-7 h-12 rounded-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {loading ? <><Loader2 className="animate-spin" size={16} /> Se evaluează…</> : <><GitCompare size={16} /> Compară acum</>}
        </button>

        {results && (
          <div className="mt-12">
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Rezultate comparative</div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border border-[#DEDCCF] bg-white">
                <thead className="bg-[#0D1F2D] text-[#F6F4EE]">
                  <tr>
                    <th className="text-left p-4 mono text-[11px] uppercase tracking-[0.18em]">Metrică</th>
                    {results.map((_, i) => <th key={i} className="text-left p-4 mono text-[11px] uppercase tracking-[0.18em]">Prop. {String(i+1).padStart(2,"0")}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DEDCCF]">
                  <TableRow label="Tip" values={results.map(r => r.input.property_type)} />
                  <TableRow label="Oraș" values={results.map(r => r.factors.city_label)} />
                  <TableRow label="Suprafață" values={results.map(r => `${r.input.surface} m²`)} />
                  <TableRow label="Valoare estimată" strong values={results.map(r => fmtEUR(r.estimated_value_eur))} />
                  <TableRow label="Preț / m²" values={results.map(r => `${r.price_per_sqm_eur} €`)} />
                  <TableRow label="Interval min" values={results.map(r => fmtEUR(r.min_value_eur))} />
                  <TableRow label="Interval max" values={results.map(r => fmtEUR(r.max_value_eur))} />
                  <TableRow label="Încredere" values={results.map(r => `${r.confidence}%`)} />
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function TableRow({ label, values, strong }) {
  return (
    <tr>
      <td className="p-4 mono text-xs text-[#52616B]">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`p-4 ${strong ? "font-display text-xl font-bold text-[#0F6E5B]" : "text-sm"}`}>{v}</td>
      ))}
    </tr>
  );
}
